import {
  Box,
  Grid,
  TextField,
  Typography,
  Button,
  FormControl,
  InputLabel,
  Select,
  MenuItem,
} from "@mui/material";
import { useTheme } from "@mui/material/styles";
import AppDialog from "./AppDialog";
import CreateOutlinedIcon from "@mui/icons-material/CreateOutlined";

function SectionBlock({ icon, title, description, children }) {
  const theme = useTheme();
  const isDark = theme.palette.mode === "dark";

  return (
    <Box
      sx={{
        border: isDark
          ? "1px solid rgba(255,255,255,0.08)"
          : "1px solid rgba(0,0,0,0.08)",
        borderRadius: 3,
        overflow: "hidden",
        background: isDark ? "rgba(255,255,255,0.04)" : "rgba(255,255,255,0.55)",
      }}
    >
      <Box
        sx={{
          px: 2,
          py: 1.4,
          display: "flex",
          alignItems: "center",
          gap: 1.2,
          borderBottom: isDark
            ? "1px solid rgba(255,255,255,0.08)"
            : "1px solid rgba(0,0,0,0.08)",
          background: isDark ? "rgba(255,255,255,0.04)" : "rgba(255,255,255,0.5)",
        }}
      >
        <Box
          sx={{
            width: 38,
            height: 38,
            borderRadius: 2.5,
            display: "grid",
            placeItems: "center",
            bgcolor: "rgba(25,118,210,0.10)",
            color: "primary.main",
            border: "1px solid rgba(25,118,210,0.16)",
            flexShrink: 0,
          }}
        >
          {icon}
        </Box>

        <Box sx={{ minWidth: 0 }}>
          <Typography sx={{ fontWeight: 900, fontSize: 15.5, lineHeight: 1.1 }}>
            {title}
          </Typography>
          {description ? (
            <Typography variant="body2" sx={{ opacity: 0.72, mt: 0.2 }}>
              {description}
            </Typography>
          ) : null}
        </Box>
      </Box>

      <Box sx={{ p: 2 }}>{children}</Box>
    </Box>
  );
}

function RenderField({ field, values, onChange }) {
  const theme = useTheme();
  const isDark = theme.palette.mode === "dark";
  const value = values?.[field.name] ?? "";

  if (field.type === "select") {
    return (
      <FormControl fullWidth>
        <InputLabel>{field.label}</InputLabel>
        <Select
          value={value}
          label={field.label}
          onChange={(e) => onChange(field.name, e.target.value)}
          disabled={field.readOnly}
        >
          {(field.options || []).map((option) => (
            <MenuItem key={option.value} value={option.value}>
              {option.label}
            </MenuItem>
          ))}
        </Select>
      </FormControl>
    );
  }

  return (
    <TextField
      label={field.label}
      type={field.type || "text"}
      value={value}
      onChange={(e) => onChange(field.name, e.target.value)}
      fullWidth
      multiline={field.multiline}
      minRows={field.minRows}
      InputLabelProps={field.type === "date" ? { shrink: true } : undefined}
      InputProps={field.readOnly ? { readOnly: true } : undefined}
      sx={
        field.readOnly
          ? {
              "& .MuiInputBase-root": {
                bgcolor: isDark ? "rgba(255,255,255,0.05)" : "rgba(0,0,0,0.03)",
              },
            }
          : undefined
      }
    />
  );
}

export default function EditDialog({
  open,
  onClose,
  onSave,
  title,
  subtitle,
  sections = [],
  values = {},
  setValues,
  saveDisabled = false,
  saveText = "Guardar",
  cancelText = "Cancelar",
}) {
  const handleFieldChange = (name, value) => {
    setValues((prev) => ({ ...prev, [name]: value }));
  };

  return (
    <AppDialog
      open={open}
      onClose={onClose}
      title={title}
      subtitle={subtitle}
      icon={<CreateOutlinedIcon />}
      maxWidth="md"
      fullScreenMobile
      stickyHeader
      contentSx={{
        p: { xs: 1.2, sm: 2 },
        background: "transparent",
      }}
      actions={
        <>
          <Button onClick={onClose} sx={{ borderRadius: 2, fontWeight: 800 }}>
            {cancelText}
          </Button>
          <Button
            onClick={onSave}
            variant="contained"
            disabled={saveDisabled}
            sx={{ borderRadius: 2, fontWeight: 900, px: 3 }}
          >
            {saveText}
          </Button>
        </>
      }
    >
      <Box sx={{ display: "flex", flexDirection: "column", gap: 2 }}>
        {sections.map((section, index) => (
          <SectionBlock
            key={index}
            icon={section.icon}
            title={section.title}
            description={section.description}
          >
            <Grid container spacing={2}>
              {section.fields.map((field) => (
                <Grid
                  key={field.name}
                  size={{
                    xs: 12,
                    md: field.md || 6,
                  }}
                >
                  <RenderField
                    field={field}
                    values={values}
                    onChange={handleFieldChange}
                  />
                </Grid>
              ))}
            </Grid>
          </SectionBlock>
        ))}
      </Box>
    </AppDialog>
  );
}
